import type { CheckTypeKey } from "../api/pricelist";
import type {
  PeriodUsage,
  PriceListUsage,
  TariffPeriod,
  UserLimits,
  UserLimitsCounters,
  UserPolicySetting,
} from "../api/user";
import { isPendingPaymentStatus } from "./userLimitsFormat";
import { isPeriodActive } from "./checksTopUp";
import {
  getCurrentActiveTariff,
  getUpcomingTariffs,
  sortTariffsByStartDate,
} from "./tariffTimeline";
import { formatTariffDate } from "./userTariffFormat";

export type TariffAccessBlockReason =
  | "no_tariff"
  | "period_expired"
  | "pending_payment"
  | "tariff_requested"
  | "upcoming_tariff"
  | "exhausted_site"
  | "exhausted_policy"
  | "exhausted_it"
  | "type_disabled"
  | "exhausted_day"
  | "exhausted_month"
  | "exhausted_year";

export type PolicyCheckBlockReason =
  | "disabled"
  | "limit_reached"
  | "period_expired"
  | "pending_payment"
  | "tariff_requested"
  | "no_tariff";

export type CheckTypeAccessKey = "site" | "policy" | "it";

export interface TariffAccessBannerContent {
  title: string;
  description: string;
}

export interface TariffAccessState {
  canRunChecks: boolean;
  canScanBySchedule: boolean;
  canEmailReport: boolean;
  canPolicyCheck: boolean;
  canItCheck: boolean;
  isLoading: boolean;
  blockReason: TariffAccessBlockReason | null;
  banner: TariffAccessBannerContent | null;
  policyCheckBlockReason: PolicyCheckBlockReason | null;
  policyCheckBanner: TariffAccessBannerContent | null;
  itCheckBlockReason: TariffAccessBlockReason | null;
  itCheckBanner: TariffAccessBannerContent | null;
}

export interface PricelistAccessFlags {
  id?: string;
  policyCheckEnabled?: boolean;
  scanByScheduleEnabled?: boolean;
  emailNotification?: boolean;
  checkTypes?: Partial<
    Record<
      CheckTypeKey,
      {
        enabled?: boolean;
        options?: { schedule?: boolean; report?: boolean };
      }
    >
  >;
}

const CHECK_PERIOD_ORDER: TariffPeriod[] = ["day", "month", "year"];

const CHECK_TYPE_TO_LIMIT: Record<CheckTypeKey, CheckTypeAccessKey> = {
  siteCheck: "site",
  policyCheck: "policy",
  it: "it",
};

const LIMIT_TO_CHECK_TYPE: Record<CheckTypeAccessKey, CheckTypeKey> = {
  site: "siteCheck",
  policy: "policyCheck",
  it: "it",
};

const NO_TARIFF_BANNER: TariffAccessBannerContent = {
  title: "Тариф не подключён",
  description: "Приобретите тариф, чтобы создавать заявки и запускать проверки.",
};

const PERIOD_EXPIRED_BANNER: TariffAccessBannerContent = {
  title: "Период тарифа истёк",
  description: "Продлите тариф или докупите новый период, чтобы снова запускать проверки.",
};

const PENDING_PAYMENT_BANNER: TariffAccessBannerContent = {
  title: "Ожидает оплаты",
  description: "Запрос на тариф создан. Завершите оплату, чтобы получить доступ к проверкам.",
};

const TARIFF_REQUESTED_BANNER: TariffAccessBannerContent = {
  title: "Тариф на рассмотрении",
  description: "Заявка отправлена администратору. Проверки станут доступны после подтверждения.",
};

const ALL_LIMITS_EXHAUSTED_BANNER: TariffAccessBannerContent = {
  title: "Лимиты проверок исчерпаны",
  description: "По текущему тарифу нет доступных проверок. Перейдите к тарифам или докупите пакет.",
};

const EXHAUSTED_BY_TYPE: Record<CheckTypeAccessKey, TariffAccessBannerContent> = {
  site: {
    title: "Лимит проверок сайтов исчерпан",
    description: "Докупите проверки сайтов или смените тариф.",
  },
  policy: {
    title: "Лимит проверок политики исчерпан",
    description: "Докупите проверки политики или смените тариф.",
  },
  it: {
    title: "Лимит проверок минцифры исчерпан",
    description: "Докупите проверки минцифры или смените тариф.",
  },
};

const TYPE_DISABLED_BY_KEY: Record<CheckTypeAccessKey, TariffAccessBannerContent> = {
  site: {
    title: "Проверка сайтов недоступна",
    description: "В текущем тарифе не включена проверка сайтов.",
  },
  policy: {
    title: "Проверка политики недоступна",
    description: "В текущем тарифе не включена проверка политики обработки ПД.",
  },
  it: {
    title: "Проверка минцифры недоступна",
    description: "В текущем тарифе не включена проверка минцифры.",
  },
};

const LEGACY_EXHAUSTED: Record<
  "exhausted_day" | "exhausted_month" | "exhausted_year",
  TariffAccessBannerContent
> = {
  exhausted_day: {
    title: "Лимит проверок на сегодня исчерпан",
    description: "Перейдите к тарифам, чтобы продлить или сменить план.",
  },
  exhausted_month: {
    title: "Лимит проверок на месяц исчерпан",
    description: "Перейдите к тарифам, чтобы продлить или сменить план.",
  },
  exhausted_year: {
    title: "Лимит проверок на год исчерпан",
    description: "Перейдите к тарифам, чтобы продлить или сменить план.",
  },
};

const toUsageNumber = (value: number | undefined): number | null => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCounterValue = (
  counters: UserLimitsCounters | undefined,
  key: CheckTypeAccessKey
): number => {
  const value = toUsageNumber(counters?.[key]);
  return value == null ? 0 : Math.max(0, value);
};

export const getRemainingByCheckType = (
  userLimits: UserLimits | null | undefined,
  key: CheckTypeAccessKey
): number | null => {
  if (!userLimits) return null;
  const apiRemaining = toUsageNumber(userLimits.remaining?.[key]);
  if (apiRemaining != null) return Math.max(0, apiRemaining);
  const limit = toUsageNumber(userLimits.limits?.[key]);
  if (limit == null) return null;
  const used = getCounterValue(userLimits.usage, key);
  return Math.max(0, limit - used);
};

const ACCESS_KEYS: readonly CheckTypeAccessKey[] = ["site", "policy", "it"];

const hasGrantedQuota = (userLimits: UserLimits, key: CheckTypeAccessKey): boolean =>
  getCounterValue(userLimits.remaining, key) > 0 ||
  getCounterValue(userLimits.limits, key) > 0 ||
  getCounterValue(userLimits.usage, key) > 0;

const hasAnyGrantedQuota = (userLimits: UserLimits): boolean =>
  ACCESS_KEYS.some((key) => hasGrantedQuota(userLimits, key));

const exhaustedReason = (
  key: CheckTypeAccessKey
): Extract<TariffAccessBlockReason, "exhausted_site" | "exhausted_policy" | "exhausted_it"> =>
  key === "site" ? "exhausted_site" : key === "policy" ? "exhausted_policy" : "exhausted_it";

const isEntitlementEnabled = (
  userLimits: UserLimits | null | undefined,
  checkType: CheckTypeKey
): boolean => {
  const entitlements = userLimits?.entitlements;
  if (!entitlements) {
    const limitKey = CHECK_TYPE_TO_LIMIT[checkType];
    return getCounterValue(userLimits?.limits, limitKey) > 0;
  }
  const config = entitlements[checkType];
  if (!config) return false;
  return config.enabled === true;
};

const hasEntitlementOption = (
  userLimits: UserLimits | null | undefined,
  option: "schedule" | "report"
): boolean => {
  const entitlements = userLimits?.entitlements;
  if (!entitlements) return false;
  return (Object.keys(CHECK_TYPE_TO_LIMIT) as CheckTypeKey[]).some((key) => {
    const config = entitlements[key];
    return config?.enabled === true && config.options?.[option] === true;
  });
};

const resolveRequestedBlock = (
  requestedTariffs: Array<{ paymentStatus?: string | null }>
): {
  blockReason: Extract<TariffAccessBlockReason, "pending_payment" | "tariff_requested">;
  banner: TariffAccessBannerContent;
} | null => {
  if (!requestedTariffs.length) return null;
  const hasPending = requestedTariffs.some((item) => isPendingPaymentStatus(item.paymentStatus));
  if (hasPending) {
    return { blockReason: "pending_payment", banner: PENDING_PAYMENT_BANNER };
  }
  return { blockReason: "tariff_requested", banner: TARIFF_REQUESTED_BANNER };
};

const noTariffState = (): TariffAccessState => ({
  ...emptyState(false),
  blockReason: "no_tariff",
  banner: NO_TARIFF_BANNER,
  policyCheckBlockReason: "no_tariff",
  policyCheckBanner: NO_TARIFF_BANNER,
  itCheckBlockReason: "no_tariff",
  itCheckBanner: NO_TARIFF_BANNER,
});

const requestedBlockState = (
  requested: NonNullable<ReturnType<typeof resolveRequestedBlock>>
): TariffAccessState => ({
  ...emptyState(false),
  blockReason: requested.blockReason,
  banner: requested.banner,
  policyCheckBlockReason:
    requested.blockReason === "pending_payment" ? "pending_payment" : "tariff_requested",
  policyCheckBanner: requested.banner,
  itCheckBlockReason: requested.blockReason,
  itCheckBanner: requested.banner,
});

const resolveTypeAccess = (
  userLimits: UserLimits,
  key: CheckTypeAccessKey
): {
  allowed: boolean;
  blockReason: TariffAccessBlockReason | null;
  banner: TariffAccessBannerContent | null;
} => {
  const checkType = LIMIT_TO_CHECK_TYPE[key];
  if (!isEntitlementEnabled(userLimits, checkType)) {
    return {
      allowed: false,
      blockReason: "type_disabled",
      banner: TYPE_DISABLED_BY_KEY[key],
    };
  }

  if (!hasGrantedQuota(userLimits, key)) {
    return {
      allowed: false,
      blockReason: "type_disabled",
      banner: TYPE_DISABLED_BY_KEY[key],
    };
  }

  const remaining = getRemainingByCheckType(userLimits, key);
  if (remaining == null || remaining <= 0) {
    return {
      allowed: false,
      blockReason: exhaustedReason(key),
      banner: EXHAUSTED_BY_TYPE[key],
    };
  }

  return { allowed: true, blockReason: null, banner: null };
};

const getUsageByPeriod = (tariff: PriceListUsage): Map<TariffPeriod, PeriodUsage> => {
  const usageByPeriod = new Map<TariffPeriod, PeriodUsage>();
  for (const item of tariff.usage ?? []) {
    if (item.period && !usageByPeriod.has(item.period)) {
      usageByPeriod.set(item.period, item);
    }
  }
  return usageByPeriod;
};

const isUsageExhausted = (period: TariffPeriod, tariff: PriceListUsage): boolean => {
  const usage = getUsageByPeriod(tariff).get(period);
  const periodLimit = toUsageNumber(usage?.periodLimit);
  if (periodLimit == null) return false;
  const counter = toUsageNumber(usage?.counter) ?? 0;
  return counter >= periodLimit;
};

const hasPeriodLimit = (tariff: PriceListUsage, period: TariffPeriod): boolean => {
  const usage = getUsageByPeriod(tariff).get(period);
  return toUsageNumber(usage?.periodLimit) != null;
};

const canLegacyTariffRunChecks = (tariff: PriceListUsage): boolean =>
  !CHECK_PERIOD_ORDER.some((period) => isUsageExhausted(period, tariff));

const getLegacyBlockReason = (tariff: PriceListUsage): TariffAccessBlockReason | null => {
  for (const period of CHECK_PERIOD_ORDER) {
    if (hasPeriodLimit(tariff, period) && isUsageExhausted(period, tariff)) {
      return `exhausted_${period}` as TariffAccessBlockReason;
    }
  }
  if (!canLegacyTariffRunChecks(tariff)) return "exhausted_day";
  return null;
};

const getLegacyRemaining = (tariff: PriceListUsage): number | null => {
  let minRemaining: number | null = null;
  for (const period of CHECK_PERIOD_ORDER) {
    const usage = getUsageByPeriod(tariff).get(period);
    const periodLimit = toUsageNumber(usage?.periodLimit);
    if (periodLimit == null) continue;
    const counter = toUsageNumber(usage?.counter) ?? 0;
    const remaining = Math.max(0, periodLimit - counter);
    minRemaining = minRemaining == null ? remaining : Math.min(minRemaining, remaining);
  }
  return minRemaining;
};

const findPricelistFlags = (
  tariff: PriceListUsage,
  pricelists: PricelistAccessFlags[]
): PricelistAccessFlags | undefined => {
  const pricelistId = tariff.pricelistId?.trim();
  if (!pricelistId) return undefined;
  return pricelists.find((item) => item.id === pricelistId);
};

const resolveLegacyPolicyAccess = (
  policySetting?: UserPolicySetting | null
): {
  canPolicyCheck: boolean;
  policyCheckBlockReason: PolicyCheckBlockReason | null;
  policyCheckBanner: TariffAccessBannerContent | null;
} => {
  if (policySetting?.policyCheckEnabled !== true) {
    return {
      canPolicyCheck: false,
      policyCheckBlockReason: "disabled",
      policyCheckBanner: TYPE_DISABLED_BY_KEY.policy,
    };
  }
  if (policySetting.limitIsReached === true) {
    return {
      canPolicyCheck: false,
      policyCheckBlockReason: "limit_reached",
      policyCheckBanner: EXHAUSTED_BY_TYPE.policy,
    };
  }
  return {
    canPolicyCheck: true,
    policyCheckBlockReason: null,
    policyCheckBanner: null,
  };
};

const getUpcomingTariffBanner = (tariffs: PriceListUsage[]): TariffAccessBannerContent => {
  const nextTariff = sortTariffsByStartDate(getUpcomingTariffs(tariffs))[0];
  const startDate = formatTariffDate(nextTariff?.startAt);
  return {
    title: "Тариф ещё не начался",
    description:
      startDate === "—"
        ? "Проверки будут доступны после начала действия тарифа."
        : `Проверки будут доступны с ${startDate}.`,
  };
};

const resolveSiteBanner = (
  blockReason: TariffAccessBlockReason | null,
  activeTariffs: PriceListUsage[]
): TariffAccessBannerContent | null => {
  if (!blockReason) return null;
  if (blockReason === "no_tariff") return NO_TARIFF_BANNER;
  if (blockReason === "period_expired") return PERIOD_EXPIRED_BANNER;
  if (blockReason === "pending_payment") return PENDING_PAYMENT_BANNER;
  if (blockReason === "tariff_requested") return TARIFF_REQUESTED_BANNER;
  if (blockReason === "upcoming_tariff") return getUpcomingTariffBanner(activeTariffs);
  if (blockReason === "exhausted_site") return EXHAUSTED_BY_TYPE.site;
  if (blockReason === "type_disabled") return TYPE_DISABLED_BY_KEY.site;
  if (blockReason === "exhausted_day" || blockReason === "exhausted_month" || blockReason === "exhausted_year") {
    return LEGACY_EXHAUSTED[blockReason];
  }
  return ALL_LIMITS_EXHAUSTED_BANNER;
};

const emptyState = (isLoading: boolean): TariffAccessState => ({
  canRunChecks: false,
  canScanBySchedule: false,
  canEmailReport: false,
  canPolicyCheck: false,
  canItCheck: false,
  isLoading,
  blockReason: null,
  banner: null,
  policyCheckBlockReason: null,
  policyCheckBanner: null,
  itCheckBlockReason: null,
  itCheckBanner: null,
});

export const getRemainingCheckCount = (
  activeTariffs: PriceListUsage[],
  userLimits?: UserLimits | null
): number | null => {
  if (userLimits && isPeriodActive(userLimits)) {
    return getRemainingByCheckType(userLimits, "site");
  }
  const currentTariff = getCurrentActiveTariff(activeTariffs);
  if (!currentTariff) return 0;
  return getLegacyRemaining(currentTariff);
};

export const getTariffAccessState = (
  activeTariffs: PriceListUsage[],
  loading: boolean,
  resolved: boolean,
  pricelists: PricelistAccessFlags[] = [],
  pricelistsResolved = true,
  policySetting?: UserPolicySetting | null,
  userLimits?: UserLimits | null,
  requestedTariffs: Array<{ paymentStatus?: string | null }> = []
): TariffAccessState => {
  if (!resolved || loading) {
    return emptyState(true);
  }

  const hasUserLimitsModel = userLimits != null;

  if (hasUserLimitsModel) {
    if (!isPeriodActive(userLimits)) {
      const requested = resolveRequestedBlock(requestedTariffs);
      if (requested) return requestedBlockState(requested);

      const expired = Boolean(userLimits.periodEnd);
      return {
        ...emptyState(false),
        blockReason: expired ? "period_expired" : "no_tariff",
        banner: expired ? PERIOD_EXPIRED_BANNER : NO_TARIFF_BANNER,
        policyCheckBlockReason: expired ? "period_expired" : "no_tariff",
        policyCheckBanner: expired ? PERIOD_EXPIRED_BANNER : NO_TARIFF_BANNER,
        itCheckBlockReason: expired ? "period_expired" : "no_tariff",
        itCheckBanner: expired ? PERIOD_EXPIRED_BANNER : NO_TARIFF_BANNER,
      };
    }

    if (!hasAnyGrantedQuota(userLimits)) {
      return noTariffState();
    }

    const siteAccess = resolveTypeAccess(userLimits, "site");
    const policyAccess = resolveTypeAccess(userLimits, "policy");
    const itAccess = resolveTypeAccess(userLimits, "it");

    return {
      canRunChecks: siteAccess.allowed,
      canPolicyCheck: policyAccess.allowed,
      canItCheck: itAccess.allowed,
      canScanBySchedule: hasEntitlementOption(userLimits, "schedule"),
      canEmailReport: hasEntitlementOption(userLimits, "report"),
      isLoading: false,
      blockReason: siteAccess.blockReason,
      banner: siteAccess.banner,
      policyCheckBlockReason: !policyAccess.allowed
        ? policyAccess.blockReason === "exhausted_policy"
          ? "limit_reached"
          : "disabled"
        : null,
      policyCheckBanner: policyAccess.banner,
      itCheckBlockReason: itAccess.blockReason,
      itCheckBanner: itAccess.banner,
    };
  }

  if (!pricelistsResolved) {
    return emptyState(true);
  }

  const requested = resolveRequestedBlock(requestedTariffs);
  if (!activeTariffs.length) {
    if (requested) return requestedBlockState(requested);

    const legacyPolicy = resolveLegacyPolicyAccess(policySetting);
    return {
      ...noTariffState(),
      ...legacyPolicy,
    };
  }

  const currentTariff = getCurrentActiveTariff(activeTariffs);
  if (!currentTariff) {
    const hasUpcoming = getUpcomingTariffs(activeTariffs).length > 0;
    const blockReason: TariffAccessBlockReason = hasUpcoming ? "upcoming_tariff" : "no_tariff";
    const legacyPolicy = resolveLegacyPolicyAccess(policySetting);
    return {
      ...emptyState(false),
      blockReason,
      banner: resolveSiteBanner(blockReason, activeTariffs),
      ...legacyPolicy,
    };
  }

  const canRunChecks = canLegacyTariffRunChecks(currentTariff);
  const blockReason = canRunChecks ? null : getLegacyBlockReason(currentTariff);
  const flags = findPricelistFlags(currentTariff, pricelists);
  const canScanBySchedule =
    currentTariff.scanByScheduleEnabled === true || flags?.scanByScheduleEnabled === true;
  const canEmailReport =
    currentTariff.emailNotification === true || flags?.emailNotification === true;
  const legacyPolicy = resolveLegacyPolicyAccess(policySetting);

  return {
    canRunChecks,
    canScanBySchedule,
    canEmailReport,
    canItCheck: false,
    isLoading: false,
    blockReason,
    banner: canRunChecks ? null : resolveSiteBanner(blockReason, activeTariffs),
    ...legacyPolicy,
    itCheckBlockReason: "type_disabled",
    itCheckBanner: TYPE_DISABLED_BY_KEY.it,
  };
};
